import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";

import { Table, Form, Button, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_STUDENT_API } from "../../../api/api";

import { FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import CertificateBG from "../../../assets/student/student-certificate.png";
import Logo from "../../../assets/Skilledition-logo.png";
import Signature from "../../../assets/student/signature.png";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";

// import totalViewsImg from "../../../assets/tutor-student-dou-icon1.png";
// import avgCompletionImg from "../../../assets/tutor-student-dou-icon1.png";
// import totalstudentImg from "../../../assets/tutor-student-dou-icon1.png";
// import AvgRatingImg from "../../../assets/tutor-student-dou-icon1.png";

// latestIcons
import totalStudents from "../../../assets/icons/admin/enrolled-students.svg";
import noOfCourses from "../../../assets/icons/student/course.svg";
import certificateIssued from "../../../assets/icons/student/certificate.svg";
import completionRatioIssued from "../../../assets/icons/tutor/average-rating.svg";

import { getPageNumbers } from "../../../utils/pagination";
import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./AdminStudentCertificates.css";

const AdminStudentCertificates = () => {
  const navigate = useNavigate();
  const certificateRef = useRef();

  const [studentCertificateStats, setStudentCertificateStats] = useState({});
  const [studentCertificates, setStudentCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  // search state
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [selectedCourseFilter, setSelectedCourseFilter] =
    useState("All Courses");
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState("All Students");
  const [selectedBatchFilter, setSelectedBatchFilter] = useState("All Batches");

  //loading
  const [loadingStudentCertificates, setLoadingStudentCertificates] =
    useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingStudentCertificates(false);
      navigate("/admin-signin");

      return;
    }

    const fetchStudentCertificate = async () => {
      try {
        const response = await axios.get(
          ADMIN_STUDENT_API.GET_ALL_STUDENT_CERTIFICATES,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setStudentCertificateStats(data.stats || {});
          setStudentCertificates(data.data || []);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error students certificate:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/admin-signin");
      } finally {
        setLoadingStudentCertificates(false);
      }
    };

    fetchStudentCertificate();
  }, [navigate]);

  // Generate PDF dynamically and open in new tab
  useEffect(() => {
    if (!selectedCertificate) return;

    const generatePDF = async () => {
      const element = certificateRef.current;
      if (!element) return;

      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("landscape", "px", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);

      const pdfBlob = pdf.output("bloburl");
      window.open(pdfBlob, "_blank", "noopener,noreferrer");
    };

    generatePDF();
  }, [selectedCertificate]);

  const handleViewCertificate = async (cert) => {
    setSelectedCertificate(cert);
  };

  const filteredCoursesForDropdown = useMemo(() => {
    return [...new Set(studentCertificates.map((s) => s.course_title))];
  }, [studentCertificates]);

  const filteredStudentsForDropdown = useMemo(() => {
    return [...new Set(studentCertificates.map((s) => s.full_name))];
  }, [studentCertificates]);

  const filteredBatchForDropdown = useMemo(() => {
    return [...new Set(studentCertificates.map((s) => s.batch))];
  }, [studentCertificates]);

  // Filter student certificates first
  const filteredStudentCertificates = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return studentCertificates.filter((s) => {
      return (
        (search === "" ||
          (s.student_reg_number || "")
            .toString()
            .toLowerCase()
            .includes(searchText) ||
          (s.certificate_number || "").toLowerCase().includes(searchText) ||
          (s.full_name || "").toLowerCase().includes(searchText) ||
          (s.email || "").toLowerCase().includes(searchText)) &&
        (selectedCourseFilter === "All Courses" ||
          (s.course_title || "").includes(selectedCourseFilter)) &&
        (selectedStudentFilter === "All Students" ||
          (s.full_name || "").includes(selectedStudentFilter)) &&
        (selectedBatchFilter === "All Batches" ||
          (s.batch || "").includes(selectedBatchFilter))
      );
    });
  }, [
    studentCertificates,
    search,
    selectedCourseFilter,
    selectedStudentFilter,
    selectedBatchFilter,
  ]);

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } = useSortableTable(
    filteredStudentCertificates,
  );

  // Pagination slice
  const studentsPerPage = 5;
  const totalPages = Math.ceil(sortedData.length / studentsPerPage);

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;

  const currentStudentCertificates = sortedData.slice(
    indexOfFirstStudent,
    indexOfLastStudent,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    selectedCourseFilter,
    selectedStudentFilter,
    selectedBatchFilter,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  const clearFilters = () => {
    setSearch("");
    setSelectedCourseFilter("All Courses");
    setSelectedStudentFilter("All Students");
    setSelectedBatchFilter("All Batches");
    setCurrentPage(1);
  };

  const handleExportStudentCertificate = () => {
    const formattedData = sortedData.map((s) => ({
      Register_ID: s.student_reg_number || "N/A",
      Certificate_Number: s.certificate_number || "N/A",
      Name: s.full_name || "N/A",
      Batch: s.batch || "N/A",
      Email: s.email || "N/A",
      Phone: s.phone_number || "N/A",
      Course: s.course_title || "N/A",
      Issue_Date: s.issued_at || "N/A",
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Student_Certificates",
      sheetName: "Certificates",
    });
  };

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  // function call
  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <div className="admin-student-management-wrapper">
      <h4 className="admin-dashboard-title">Student Certification</h4>
      <p className="admin-dashboard-subtitle">
        View all certified students and access their certificate details 
      </p>

      <Row className="admin-student-section-cards-row">
        <Col lg={3} md={4} sm={6} xs={12} className="mb-3">
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">
              Enrolled Students
            </h6>
            <img
              src={totalStudents}
              className="admin-student-stat-icon"
              alt="students"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentCertificateStats.enrolled_students) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">
              +18% from last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">Active Courses</h6>
            <img
              src={noOfCourses}
              className="admin-student-stat-icon"
              alt="Courses"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentCertificateStats.no_of_courses) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">
              85% engagement
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">
              Certificate Issued
            </h6>
            <img
              src={certificateIssued}
              className="admin-student-stat-icon"
              alt="certificate Issued"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentCertificateStats.certificate_issued_students) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">On track</span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">
              Certificate Completion
            </h6>
            <img
              src={completionRatioIssued}
              className="admin-student-stat-icon"
              alt="completion ratio"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentCertificateStats.completion_ratio) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">On track</span>
          </div>
        </Col>
      </Row>

      <div className="admin-student-management-filters">
        <Row className="mb-3 admin-student-management-filters-rowsss">
          <Col md={4}>
            <div className="admin-student-management-search-box">
              <span className="admin-student-management-search-icon">
                <IoSearchOutline />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by enroll id, certificate number, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-student-management-search-input"
              />
            </div>
          </Col>

          <Col md={2}>
            <Form.Select
              value={selectedCourseFilter}
              onChange={(e) => setSelectedCourseFilter(e.target.value)}
            >
              <option value="All Courses">All Courses</option>
              {filteredCoursesForDropdown.map((course, index) => (
                <option key={index} value={course}>
                  {course}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={2}>
            <Form.Select
              value={selectedStudentFilter}
              onChange={(e) => setSelectedStudentFilter(e.target.value)}
            >
              <option value="All Students">All Students</option>
              {filteredStudentsForDropdown.map((student, index) => (
                <option key={index} value={student}>
                  {student}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={2}>
            <Form.Select
              value={selectedBatchFilter}
              onChange={(e) => setSelectedBatchFilter(e.target.value)}
            >
              <option value="All Batches">All Batches</option>
              {filteredBatchForDropdown.map((batch, index) => (
                <option key={index} value={batch}>
                  {batch}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={1}>
            <Button
              className="admin-student-management-clear-btn"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Col>

          <Col md={1}>
            <Button
              size="sm"
              className="admin-student-management-table-export"
              onClick={handleExportStudentCertificate}
              disabled={sortedData.length === 0}
              title={
                sortedData.length === 0
                  ? "No data available to export"
                  : "Export to Excel"
              }
              style={{
                cursor: sortedData.length === 0 ? "not-allowed" : "pointer",
                opacity: sortedData.length === 0 ? 0.5 : 1,
              }}
            >
              <img
                src={excelImg}
                alt="Excel"
                className="admin-student-management-table-export-excel-img"
              />
            </Button>
          </Col>
        </Row>
      </div>

      {/* TABLE  */}
      <div className="admin-student-management-table-wrapper">
        <Table className="admin-student-management-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("student_reg_number")}
                style={{ cursor: "pointer" }}
              >
                Enroll Id {renderSortIcon("student_reg_number")}
              </th>
              <th>Certificate Number</th>
              <th
                onClick={() => handleSort("full_name")}
                style={{ cursor: "pointer" }}
              >
                Student {renderSortIcon("full_name")}
              </th>
              <th>Batch</th>
              <th
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer" }}
              >
                Email {renderSortIcon("email")}
              </th>
              <th>Phone</th>
              <th
                onClick={() => handleSort("course_title")}
                style={{ cursor: "pointer" }}
              >
                Course {renderSortIcon("course_title")}
              </th>

              <th
                onClick={() => handleSort("issued_at")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Issue Date {renderSortIcon("issued_at")}
                </div>
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loadingStudentCertificates ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>
                  Loading students...
                </td>
              </tr>
            ) : currentStudentCertificates.length > 0 ? (
              currentStudentCertificates.map((s) => (
                <tr key={s.certificate_id}>
                  <td title={s.student_reg_number}>
                    {s.student_reg_number || "-"}
                  </td>
                  <td title={s.certificate_number}>{s.certificate_number}</td>
                  <td title={s.full_name}>{s.full_name || "-"}</td>
                  <td title={s.batch}>{s.batch || "-"}</td>
                  <td title={s.email}>{s.email || "-"}</td>
                  <td title={s.phone_number}>{s.phone_number || "-"}</td>
                  <td title={s.course_title}>{s.course_title || "-"}</td>
                  <td title={s.issued_at}>{s.issued_at || "-"}</td>
                  <td>
                    <div className="admin-student-management-action-icons">
                      <FaEye
                        title="View Certificate"
                        className="admin-student-management-eye"
                        onClick={() => handleViewCertificate(s)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>
                  <img
                    src={Nofound}
                    alt="No Data Found"
                    className="admin-all-tutor-empty-img"
                  />
                  <p className="admin-dashboard-no-data">No Data Found</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Hidden certificate template */}
      <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
        <div ref={certificateRef} className="certificate-wrapper">
          <img
            src={CertificateBG}
            alt="certificate"
            className="certificate-bg"
          />
          <div className="certificate-overlay">
            <img src={Logo} alt="logo" className="certificate-logo" />

            <h2 className="certificate-main-title">CERTIFICATE</h2>
            <p className="certificate-subtitle">OF ACHIEVEMENT</p>

            <p className="certificate-presented">PROUDLY PRESENTED TO</p>

            <h1 className="certificate-name">
              {selectedCertificate?.full_name}
            </h1>

            <p className="certificate-course">
              {selectedCertificate?.course_title}
            </p>

            <p className="certificate-date">{selectedCertificate?.issued_at}</p>

            <div className="certificate-footer">
              <div className="signature-block">
                <p className="signature-title">
                  This certificate was awarded by :
                </p>
                <div className="signature-line">
                  <img src={Signature} alt="signature" />
                </div>
                <p className="signature-title">Managing Director</p>
              </div>
            </div>

            <p className="certificate-id">
              Certificate ID: {selectedCertificate?.certificate_number}
            </p>
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="tutor-assignment-table-pagination">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {pages.map((page, idx) =>
            page === "..." ? (
              <span key={idx} style={{ padding: "0 3px" }}>
                ...
              </span>
            ) : (
              <Button
                key={idx}
                active={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ),
          )}

          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminStudentCertificates;
